/**
 * DateInput Component
 *
 * A styled date input with consistent design system styling.
 * Uses native date input for simplicity.
 */

import React from 'react';
import { Box, Typography, InputBase, InputAdornment, IconButton } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import type { SxProps, Theme } from '@mui/material';
import { colors, typography as typographyTokens, radii } from '../../tokens';

export interface DateInputProps {
    /** Field label */
    label?: string;
    /** Current value (ISO date string: YYYY-MM-DD) */
    value: string;
    /** Change handler */
    onChange: (value: string) => void;
    /** Whether the field is disabled */
    disabled?: boolean;
    /** Error state */
    error?: boolean;
    /** Helper text */
    helperText?: string;
    /** Minimum date */
    min?: string;
    /** Maximum date */
    max?: string;
    /** Full width */
    fullWidth?: boolean;
    /** Additional styles */
    sx?: SxProps<Theme>;
}

export const DateInput: React.FC<DateInputProps> = ({
    label,
    value,
    onChange,
    disabled = false,
    error = false,
    helperText,
    min,
    max,
    fullWidth = false,
    sx,
}) => (
    <Box sx={{ width: fullWidth ? '100%' : 'auto', ...sx }}>
        {label && (
            <Typography
                component='label'
                variant='body2'
                sx={{
                    display: 'block',
                    mb: '6px',
                    fontWeight: typographyTokens.fontWeight.medium,
                    color: error ? colors.error.main : colors.neutral[700],
                    fontSize: typographyTokens.fontSize.sm,
                }}>
                {label}
            </Typography>
        )}

        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: disabled ? colors.neutral[50] : colors.neutral[0],
                border: `1px solid ${error ? colors.error.main : colors.neutral[300]}`,
                borderRadius: `${radii.md}px`,
                px: 1.5,
                height: 40,
                '&:hover': {
                    borderColor: disabled ? colors.neutral[300] : error ? colors.error.dark : colors.neutral[400],
                },
                '&:focus-within': {
                    borderColor: error ? colors.error.main : colors.primary[500],
                    borderWidth: 2,
                    px: `${12 - 1}px`,
                },
                opacity: disabled ? 0.7 : 1,
            }}>
            <InputBase
                type='date'
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                inputProps={{ min, max }}
                fullWidth
                sx={{
                    fontSize: typographyTokens.fontSize.sm,
                    color: colors.neutral[900],
                    '& .MuiInputBase-input': {
                        padding: 0,
                        '&::-webkit-calendar-picker-indicator': {
                            display: 'none',
                        },
                        '&::-webkit-inner-spin-button': {
                            display: 'none',
                        },
                    },
                }}
            />
            <CalendarTodayIcon sx={{ color: colors.neutral[400], fontSize: 18, ml: 1, flexShrink: 0 }} />
        </Box>

        {helperText && (
            <Typography
                sx={{
                    color: error ? colors.error.main : colors.neutral[500],
                    fontSize: typographyTokens.fontSize.xs,
                    mt: 0.5,
                }}>
                {helperText}
            </Typography>
        )}
    </Box>
);

export default DateInput;
